import useNswagClient from '../../../hooks/api/useNswagClient';
import { Item, Recipe } from '../../../app/services/api/generated';
import { useState, useCallback, useRef } from 'react';
import debounce from 'lodash/debounce';
import axios, { CancelTokenSource } from 'axios';

export interface StockItemWithRecipeFlag extends Item {
  isRecipe?: boolean;
}

const useStockItemsData = () => {
  const { getItems, getRecipes } = useNswagClient();
  const [stockItems, setStockItems] = useState<StockItemWithRecipeFlag[]>([]);
  const [recipeItems, setRecipeItems] = useState<StockItemWithRecipeFlag[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const cancelTokenSourceRef = useRef<CancelTokenSource | null>(null);

  const debouncedSearch = useCallback(
    debounce((search: string, storeNumber: string) => {
      if (cancelTokenSourceRef.current) {
        cancelTokenSourceRef.current.cancel('Cancelled due to new search.');
      }

      if (!search.trim()) {
        setStockItems([]);
        setRecipeItems([]);
        setIsSearching(false);
        return;
      }

      cancelTokenSourceRef.current = axios.CancelToken.source();
      const token = cancelTokenSourceRef.current.token;

      setIsSearching(true);

      Promise.all([
        getItems(search, storeNumber, undefined, undefined, undefined, true, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, token),
        getRecipes(search, storeNumber, 0, 25, true, token),
      ])
        .then(([itemsRes, recipesRes]) => {
          setStockItems((itemsRes?.data?.items ?? []).map(item => ({ ...item, isRecipe: false })));
          setRecipeItems((recipesRes?.data ?? []).map((recipe: Recipe) => ({
            id: recipe.id,
            itemNumber: recipe.itemNumber,
            description: recipe.description,
            costPrice: recipe.costPriceOnHand ?? 0,
            primaryCategory: recipe.primaryCategory,
            status: recipe.status,
            isRecipe: true,
            isBom: true,
          })));
          setIsSearching(false);
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            console.error(error);
            setIsSearching(false);
          }
        });
    }, 500),
    [],
  );

  return {
    stockItems,
    setStockItems,
    recipeItems,
    isSearching,
    debouncedSearch,
  };
};

export default useStockItemsData;